// components/common/Card/GlassCard.tsx
import { ReactNode } from 'react';
import { Paper, Box, Typography, PaperProps, BoxProps } from '@mui/material';
import { styled, alpha, SxProps, Theme } from '@mui/material/styles';

// Styled Paper props interface
interface StyledPaperProps {
  borderColor?: string;
  hoverEffect?: boolean;
  animate?: boolean;
  glowColor?: string;
}

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) =>
    !['borderColor', 'hoverEffect', 'animate', 'glowColor'].includes(prop as string),
})<StyledPaperProps>(({ theme, borderColor, hoverEffect, animate, glowColor }) => ({
  backgroundColor: theme.palette.background.paper,
  border: '1px solid',
  borderColor: borderColor || 'rgba(255, 255, 255, 0.1)',
  borderRadius: '12px',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  backgroundImage: 'none',

  ...(animate && {
    animation: 'fade-in 0.5s ease-out',
  }),

  ...(hoverEffect && {
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
      borderColor: theme.palette.primary.main,
    },
  }),

  ...(glowColor && {
    '&:hover': {
      boxShadow: `0 8px 32px ${alpha(glowColor, 0.3)}`,
      borderColor: glowColor,
    },
  }),
}));

export interface GlassCardProps extends Omit<PaperProps, 'elevation'> {
  children: ReactNode;
  borderColor?: string;
  hoverEffect?: boolean;
  animate?: boolean;
  glowColor?: string;
  sx?: SxProps<Theme>;
}

/**
 * GlassCard - A styled card component with glassmorphism effect
 */
export function GlassCard({
  children,
  borderColor,
  hoverEffect = false,
  animate = false,
  glowColor,
  sx = {},
  ...props
}: GlassCardProps) {
  return (
    <StyledPaper
      elevation={0}
      borderColor={borderColor}
      hoverEffect={hoverEffect}
      animate={animate}
      glowColor={glowColor}
      sx={sx}
      {...props}
    >
      {children}
    </StyledPaper>
  );
}

type CardColor = 'primary' | 'secondary' | 'warning' | 'error' | 'success' | 'info';

export interface CardHeaderProps extends Omit<BoxProps, 'title'> {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  color?: CardColor;
  gradient?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * CardHeader - Styled header for GlassCard
 */
export function CardHeader({
  title,
  subtitle,
  action,
  color = 'primary',
  gradient = false,
  sx = {},
  ...props
}: CardHeaderProps) {
  return (
    <Box
      sx={{
        p: 2,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...sx,
      }}
      {...props}
    >
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Rajdhani", sans-serif',
            fontWeight: 600,
            ...(gradient ? {
              background: `linear-gradient(90deg, var(--accent-${color}), var(--accent-info))`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            } : {
              color: `var(--accent-${color === 'primary' ? 'primary' : color})`,
            }),
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
  );
}

export interface CardContentProps extends BoxProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * CardContent - Styled content area for GlassCard
 */
export function CardContent({ children, sx = {}, ...props }: CardContentProps) {
  return (
    <Box sx={{ p: 2, ...sx }} {...props}>
      {children}
    </Box>
  );
}

export interface CardFooterProps extends BoxProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * CardFooter - Styled footer for GlassCard
 */
export function CardFooter({ children, sx = {}, ...props }: CardFooterProps) {
  return (
    <Box
      sx={{
        p: 2,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

export default GlassCard;
